import clsx from 'clsx';
import { DropdownMenu } from 'radix-ui';
import { memo, useCallback, useMemo, useRef } from 'react';
import { LuCheck, LuChevronDown } from 'react-icons/lu';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type MultiSelectOption = {
  /** Stable option value */
  value: string;
  /** Human-readable option label */
  label: string;
  /** Whether this option can be toggled */
  disabled?: boolean;
};

type MultiSelectSummaryParams = {
  selectedOptions: MultiSelectOption[];
  allOptions: MultiSelectOption[];
  maxSummaryItems: number;
  placeholder: string;
};

type MultiSelectDropdownProps = {
  /** Trigger label shown before the summary */
  label: string;
  /** Available options */
  options: MultiSelectOption[];
  /** Controlled selected values */
  selectedValues: string[];
  /** Controlled selected values callback */
  onSelectedValuesChange: (values: string[]) => void;
  /** Summary placeholder when nothing is selected */
  placeholder?: string;
  /** Max selected labels to show before collapsing to count */
  maxSummaryItems?: number;
  /** Trigger disabled state */
  disabled?: boolean;
  /** Optional custom summary formatter */
  getSummaryLabel?: (params: MultiSelectSummaryParams) => string;
  /** Keep menu open while selecting */
  closeOnSelect?: boolean;
  /** Show select all action */
  showSelectAll?: boolean;
  /** Show clear action */
  showClearAll?: boolean;
  /** Message when there are no options */
  emptyMessage?: string;
  /** Optional explicit aria-label for the trigger */
  ariaLabel?: string;
};

type CheckboxOptionItemProps = {
  option: MultiSelectOption;
  checked: boolean;
  closeOnSelect: boolean;
  onToggle: (value: string, checked: boolean) => void;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const DEFAULT_PLACEHOLDER = 'All';
const DEFAULT_EMPTY_MESSAGE = 'No options available';

/* -------------------------------------------------------------------------- */
/*                               Helper functions                             */
/* -------------------------------------------------------------------------- */

const getDefaultSummaryLabel = ({
  selectedOptions,
  allOptions,
  maxSummaryItems,
  placeholder,
}: MultiSelectSummaryParams): string => {
  if (!selectedOptions.length) return placeholder;

  if (selectedOptions.length === allOptions.length) {
    return 'All selected';
  }

  if (selectedOptions.length <= maxSummaryItems) {
    return selectedOptions.map((option) => option.label).join(', ');
  }

  return `${selectedOptions.length} selected`;
};

const toggleSelection = (currentValues: string[], optionValue: string, checked: boolean): string[] => {
  if (checked) {
    return currentValues.includes(optionValue) ? currentValues : [...currentValues, optionValue];
  }

  return currentValues.filter((value) => value !== optionValue);
};

/* -------------------------------------------------------------------------- */
/*                              Sub-components                                */
/* -------------------------------------------------------------------------- */

/**
 * Memoised row for a single checkbox option.
 * Prevents all rows from re-rendering when a sibling is toggled.
 */
const CheckboxOptionItem = memo(({ option, checked, closeOnSelect, onToggle }: CheckboxOptionItemProps) => (
  <DropdownMenu.CheckboxItem
    className={clsx(
      'relative flex select-none items-center rounded-sm px-3 py-2 ps-8 text-foreground text-sm outline-0',
      'hover:bg-muted focus-visible:bg-muted',
      option.disabled && 'cursor-not-allowed text-muted-foreground hover:bg-transparent',
    )}
    checked={checked}
    disabled={option.disabled}
    onCheckedChange={(nextChecked) => onToggle(option.value, Boolean(nextChecked))}
    onSelect={(event) => {
      if (!closeOnSelect) event.preventDefault();
    }}
  >
    <DropdownMenu.ItemIndicator className="absolute left-2 inline-flex items-center">
      <LuCheck className="size-4" />
    </DropdownMenu.ItemIndicator>
    {option.label}
  </DropdownMenu.CheckboxItem>
));
CheckboxOptionItem.displayName = 'CheckboxOptionItem';

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Multi-select dropdown component built on top of Radix UI's DropdownMenu and a custom CheckboxItem.
 * Wrapped in `memo()` so re-renders are skipped when props are unchanged. The optimisation is only
 * effective when the consumer passes stable references — wrap inline callbacks like `getSummaryLabel`
 * or `onSelectedValuesChange` in `useCallback` at the call site if parent re-renders are frequent.
 *
 * @example
 * ```tsx
 * const options = [
 *   { value: 'blast-a', label: 'Blast A' },
 *   { value: 'blast-b', label: 'Blast B' },
 *   { value: 'blast-c', label: 'Blast C' },
 * ];
 *
 * const [selectedValues, setSelectedValues] = useState<string[]>([]);
 *
 * <MultiSelectDropdown
 *   label="Blasts"
 *   options={options}
 *   selectedValues={selectedValues}
 *   onSelectedValuesChange={setSelectedValues}
 *   placeholder="None selected"
 * />
 * ```
 */
const MultiSelectDropdown = memo(
  ({
    label,
    options,
    selectedValues,
    onSelectedValuesChange,
    placeholder = DEFAULT_PLACEHOLDER,
    maxSummaryItems = 2,
    disabled,
    getSummaryLabel,
    closeOnSelect = false,
    showSelectAll = true,
    showClearAll = true,
    emptyMessage = DEFAULT_EMPTY_MESSAGE,
    ariaLabel,
  }: MultiSelectDropdownProps) => {
    const selectedValuesRef = useRef(selectedValues);
    selectedValuesRef.current = selectedValues;

    // Memoised Set for O(1) checked lookups inside the options map
    const selectedValueSet = useMemo(() => new Set(selectedValues), [selectedValues]);

    const selectedOptions = useMemo(
      () => options.filter((option) => selectedValueSet.has(option.value)),
      [options, selectedValueSet],
    );

    const summary = (getSummaryLabel ?? getDefaultSummaryLabel)({
      selectedOptions,
      allOptions: options,
      maxSummaryItems,
      placeholder,
    });

    const handleToggle = useCallback(
      (value: string, checked: boolean) => {
        onSelectedValuesChange(toggleSelection(selectedValuesRef.current, value, checked));
      },
      [onSelectedValuesChange],
    );

    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className={clsx(
              'flex w-full items-center justify-between rounded-md border border-border bg-background px-5 py-2.5 text-left',
              'text-foreground text-sm',
              'focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2',
              'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
            )}
            type="button"
            disabled={disabled}
            aria-label={ariaLabel ?? label}
          >
            <span className="truncate">
              <span className="font-medium">{label}</span>
              <span className="text-muted-foreground">: {summary}</span>
            </span>
            <LuChevronDown className="ms-2 shrink-0 text-muted-foreground" size={18} />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="z-10 min-w-(--radix-dropdown-menu-trigger-width) rounded-md border border-border bg-background p-2 shadow-md"
            sideOffset={4}
            align="start"
          >
            {options.length ? (
              <>
                <div className="max-h-60 overflow-auto">
                  {options.map((option) => (
                    <CheckboxOptionItem
                      key={option.value}
                      option={option}
                      checked={selectedValueSet.has(option.value)}
                      closeOnSelect={closeOnSelect}
                      onToggle={handleToggle}
                    />
                  ))}
                </div>

                {(showSelectAll || showClearAll) && (
                  <>
                    <DropdownMenu.Separator className="my-2 border-0 border-border border-b" />
                    <div className="flex items-center justify-between gap-2 px-1">
                      {showSelectAll ? (
                        <DropdownMenu.Item
                          className="cursor-pointer rounded-sm px-2 py-1 text-primary text-xs outline-0 hover:bg-muted focus-visible:bg-muted"
                          onSelect={(event) => {
                            event.preventDefault();
                            onSelectedValuesChange(
                              options.filter((option) => !option.disabled).map((option) => option.value),
                            );
                          }}
                        >
                          Select all
                        </DropdownMenu.Item>
                      ) : (
                        <span />
                      )}

                      {showClearAll && (
                        <DropdownMenu.Item
                          className="cursor-pointer rounded-sm px-2 py-1 text-primary text-xs outline-0 hover:bg-muted focus-visible:bg-muted"
                          onSelect={(event) => {
                            event.preventDefault();
                            onSelectedValuesChange([]);
                          }}
                        >
                          Clear all
                        </DropdownMenu.Item>
                      )}
                    </div>
                  </>
                )}
              </>
            ) : (
              <p className="px-3 py-2 text-muted-foreground text-sm">{emptyMessage}</p>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    );
  },
);
MultiSelectDropdown.displayName = 'MultiSelectDropdown';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { MultiSelectDropdownProps, MultiSelectOption, MultiSelectSummaryParams };
export { MultiSelectDropdown };
